import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import NavigationDropdown from "../../components/NavigationDropdown";
import CaseCard from "../../components/CaseCard";
import Footer from "../../components/Footer.js";
import { getCurrentUser } from "../../utils/getCurrentUser";

const DefenceAndCaseHandling_1 = () => {
  const navigate = useNavigate();
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const user = getCurrentUser();
  const [caseItem, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const API_URL = process.env.REACT_APP_API_URL;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; // 3 columns × 3 rows

  const closeNavigationDropdown = useCallback(
    () => setNavigationDropdownOpen(false),
    [],
  );
  const closeLogoutConfirmation = useCallback(
    () => setLogoutConfirmationOpen(false),
    [],
  );

  const fetchUserCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/api/cases/lists/${user?.id}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok)
        throw new Error(`Failed to fetch cases: ${response.statusText}`);
      const data = await response.json();
      const casesArray = data.cases || [];
      const transformedCases = casesArray.map((c) => ({
        id: c._id,
        name: c.name,
        status: c.status,
        price: c.amount,
        type: c.type,
        dateCreated: c.createdAt,
        dueDate: c.dueDate,
        reference: c.invoiceNumber,
        accountNumber: c.accountNumber,
        autoIncrementAccount: c.autoIncrementAccount,
        businessName: c.businessName,
        caseType: c.caseType,
        currency: c.currency,
        email: c.email,
        invoiceId: c.invoiceId,
        address: c.address,
      }));
      setCases(transformedCases);
    } catch (err) {
      console.error("Error fetching user cases:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, API_URL]);

  useEffect(() => {
    if (user?.id) fetchUserCases();
  }, [user?.id, fetchUserCases]);

  const totalCases = caseItem.length;
  const pendingCases = caseItem.filter((c) => c.status !== "Resolved").length;
  const resolvedCases = caseItem.filter((c) => c.status === "Resolved").length;

  // Filtering
  const filteredCases = caseItem.filter((c) => {
    const matchesSearch = (c.name || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "pending" && c.status !== "Resolved") ||
      (filterStatus === "resolved" && c.status === "Resolved");
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);

  // Reset page to 1 when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus]);

  // Ensure current page is within valid range when total pages changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentCases = filteredCases.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');

        html, body { background: rgb(241,245,249) !important; }

        .dd-root * { box-sizing: border-box; }
        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: rgb(241,245,249) !important;
          min-height: 100vh;
        }

        .dd-card {
          background: #ffffff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 2px 12px rgba(0,0,0,0.04);
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .dd-card-hoverable { cursor: default; }
        .dd-card-hoverable:hover { transform: translateY(-3px); box-shadow: 0 4px 24px rgba(0,0,0,0.09); }

        .dd-accent-bar { position: absolute; top: 0; left: 0; right: 0; height: 4px; border-radius: 16px 16px 0 0; }

        .dd-tab-row { display: inline-flex; background: #F1F5F9; border-radius: 8px; padding: 3px; gap: 2px; }
        .dd-tab { padding: 6px 16px; border-radius: 6px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748B; cursor: pointer; border: none; background: transparent; transition: background 0.15s, color 0.15s; }
        .dd-tab:hover { background: #ffffff; color: #0F172A; }
        .dd-tab.dd-active { background: #ffffff; color: #2563EB; box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

        .dd-search { display: flex; align-items: center; gap: 8px; background: #ffffff; border: 1px solid #E2E8F0; border-radius: 8px; padding: 0 14px; width: 260px; transition: border-color 0.15s, box-shadow 0.15s; }
        .dd-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .dd-search input { border: none; outline: none; background: transparent; font-family: 'DM Sans', sans-serif; font-size: 14px; color: #0F172A; width: 100%; padding: 9px 0; }
        .dd-search input::placeholder { color: #94A3B8; }

        .dd-count-pill { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; background: #EFF6FF; border-radius: 20px; font-size: 12px; font-weight: 700; color: #2563EB; }

        .dd-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; gap: 12px; }

        .dd-retry-btn { padding: 8px 18px; border-radius: 8px; border: none; background: #2563EB; color: #ffffff; font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
        .dd-retry-btn:hover { background: #1D4ED8; }

        .dd-cases-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 18px; }

        .dd-spinner { width: 36px; height: 36px; border: 3px solid #E2E8F0; border-top-color: #2563EB; border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Pagination */
        .dd-page-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; background: #ffffff;
          border: 1px solid #E2E8F0; border-radius: 8px;
          font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A;
          cursor: pointer; transition: border-color 0.15s, background 0.15s, color 0.15s;
        }
        .dd-page-btn:hover:not(:disabled) { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
        .dd-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        .dd-page-number {
          width: 32px; height: 32px; border-radius: 8px; border: 1px solid;
          background: #ffffff; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
          cursor: pointer; transition: all 0.15s;
          display: flex; align-items: center; justify-content: center;
        }
      `}</style>

      <div
        className="dd-root w-full overflow-visible"
        style={{ background: "rgb(241,245,249)", minHeight: "100vh" }}
      >
        <NavigationPanel />

        <div
          style={{
            position: "absolute",
            top: 136,
            width: "100%",
            padding: "0 96px 64px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* Page header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#0F172A",
                }}
              >
                Defence and Case Handling
              </h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748B" }}>
                Acknowledge defences and revert back.
              </p>
            </div>
          </div>

          {/* Metric cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 18,
            }}
          >
            {[
              {
                label: "Total cases",
                value: totalCases,
                accent: "#7C3AED",
                iconBg: "#F5F3FF",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"
                      stroke="#7C3AED"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="m3.27 6.96 8.73 5.05 8.73-5.05M12 22.08V12"
                      stroke="#7C3AED"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
              {
                label: "Pending defences",
                value: pendingCases,
                accent: "#F59E0B",
                iconBg: "#FFFBEB",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 9v5M12 21.41H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12v-.01Z"
                      stroke="#D97706"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.995 17h.009"
                      stroke="#D97706"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
              {
                label: "Resolved cases",
                value: resolvedCases,
                accent: "#059669",
                iconBg: "#ECFDF5",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
                      stroke="#059669"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="m7.75 12 2.83 2.83 5.67-5.66"
                      stroke="#059669"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ),
              },
            ].map((card) => (
              <div
                key={card.label}
                className="dd-card dd-card-hoverable"
                style={{ padding: 24 }}
              >
                <div
                  className="dd-accent-bar"
                  style={{ background: card.accent }}
                />
                <div style={{ marginTop: 8 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: card.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 18,
                      border: "1px solid #E2E8F0",
                    }}
                  >
                    {card.icon}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 6,
                    }}
                  >
                    {card.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 28,
                      fontWeight: 500,
                      color: "#0F172A",
                      lineHeight: 1.1,
                    }}
                  >
                    {card.value}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cases section */}
          <div className="dd-card" style={{ overflow: "hidden" }}>
            {/* Section header */}
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid #E2E8F0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}
                >
                  Cases with pending defences
                </span>
                <span className="dd-count-pill">
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "#2563EB",
                      display: "inline-block",
                    }}
                  />
                  {filteredCases.length === 1
                    ? "1 case"
                    : `${filteredCases.length || "No"} cases`}
                </span>
              </div>
            </div>

            {/* Filter row */}
            <div
              style={{
                padding: "12px 24px",
                borderBottom: "1px solid #F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#FAFBFD",
              }}
            >
              <div className="dd-tab-row">
                {[
                  { key: "all", label: "View all" },
                  { key: "pending", label: "Pending" },
                  { key: "resolved", label: "Resolved" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`dd-tab${filterStatus === tab.key ? " dd-active" : ""}`}
                    onClick={() => setFilterStatus(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="dd-search">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2"
                    stroke="#94A3B8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search cases…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Cases grid / states */}
            <div style={{ padding: 24 }}>
              {loading ? (
                <div className="dd-empty">
                  <div className="dd-spinner" />
                  <span style={{ fontSize: 14, color: "#64748B" }}>
                    Loading cases…
                  </span>
                </div>
              ) : error ? (
                <div className="dd-empty">
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "#FEF2F2",
                      border: "1px solid #FECACA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 9v5M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                        stroke="#DC2626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span
                    style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}
                  >
                    Failed to load cases
                  </span>
                  <span style={{ fontSize: 13, color: "#64748B" }}>
                    {error}
                  </span>
                  <button className="dd-retry-btn" onClick={fetchUserCases}>
                    Try again
                  </button>
                </div>
              ) : filteredCases.length > 0 ? (
                <>
                  <div className="dd-cases-grid">
                    {currentCases.map((caseData) => (
                      <CaseCard
                        key={caseData.id}
                        initialName={caseData.name}
                        initialStatus={caseData.status}
                        initialPrice={caseData.price}
                        caseType={caseData.type}
                        reference={caseData.reference}
                        dateCreated={caseData.dateCreated}
                        dueDate={caseData.dueDate}
                        defenceStatus={caseData.defenceStatus}
                        lastAction={caseData.lastAction}
                        caseId={caseData.id}
                        contactInfo={{
                          country: caseData.address?.country,
                          city: caseData.address?.city,
                          house: caseData.address?.house,
                          street: caseData.address?.street,
                          postalCode: caseData.address?.postalCode,
                          phone: caseData.phone || "+441234567890",
                          email: caseData.email || "pm@think-ai.io",
                          company: caseData.businessName || "",
                        }}
                        claimDetails={{
                          amountOwed: `£${caseData.price}`,
                          natureOfClaim:
                            caseData.natureOfClaim ||
                            "Goods sold and delivered",
                          communicationHistory:
                            caseData.communicationHistory ||
                            "Initial LBA sent, no response",
                        }}
                      />
                    ))}
                  </div>

                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: 20,
                        borderTop: "1px solid #E2E8F0",
                        marginTop: 20,
                      }}
                    >
                      <button
                        className="dd-page-btn"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                        Previous
                      </button>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {/* Page 1 always shown */}
                        <button
                          className="dd-page-number"
                          style={{
                            borderColor:
                              1 === currentPage ? "#2563EB" : "#E2E8F0",
                            background:
                              1 === currentPage ? "#EFF6FF" : "#ffffff",
                            color: 1 === currentPage ? "#2563EB" : "#1E293B",
                          }}
                          onClick={() => handlePageChange(1)}
                        >
                          1
                        </button>

                        {/* Ellipsis before sliding window if needed */}
                        {currentPage > 4 && totalPages > 7 && (
                          <span style={{ padding: "0 2px", color: "#94A3B8" }}>
                            …
                          </span>
                        )}

                        {/* Sliding window around current page */}
                        {Array.from({ length: 5 }, (_, i) => {
                          const start = Math.max(2, currentPage - 2);
                          const end = Math.min(totalPages - 1, currentPage + 2);
                          const page = start + i;
                          if (page > end) return null;
                          return (
                            <button
                              key={page}
                              className="dd-page-number"
                              style={{
                                borderColor:
                                  page === currentPage ? "#2563EB" : "#E2E8F0",
                                background:
                                  page === currentPage ? "#EFF6FF" : "#ffffff",
                                color:
                                  page === currentPage ? "#2563EB" : "#1E293B",
                              }}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        })}

                        {/* Ellipsis after sliding window if needed */}
                        {currentPage < totalPages - 3 && totalPages > 7 && (
                          <span style={{ padding: "0 2px", color: "#94A3B8" }}>
                            …
                          </span>
                        )}

                        {/* Last page – only shown if totalPages > 1 and last page is not already in the window */}
                        {totalPages > 1 && (
                          <button
                            className="dd-page-number"
                            style={{
                              borderColor:
                                totalPages === currentPage
                                  ? "#2563EB"
                                  : "#E2E8F0",
                              background:
                                totalPages === currentPage
                                  ? "#EFF6FF"
                                  : "#ffffff",
                              color:
                                totalPages === currentPage
                                  ? "#2563EB"
                                  : "#1E293B",
                            }}
                            onClick={() => handlePageChange(totalPages)}
                          >
                            {totalPages}
                          </button>
                        )}
                      </div>

                      <button
                        className="dd-page-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="dd-empty">
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "#F1F5F9",
                      border: "1px solid #E2E8F0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"
                        stroke="#94A3B8"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span
                    style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}
                  >
                    No cases found
                  </span>
                  <span style={{ fontSize: 13, color: "#64748B" }}>
                    {searchQuery
                      ? "Try adjusting your search."
                      : "Cases will appear here once submitted."}
                  </span>
                </div>
              )}
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {isNavigationDropdownOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeNavigationDropdown}
        >
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113,113,113,0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default DefenceAndCaseHandling_1;
